import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";
import {
  FaWallet,
  FaTimes,
  FaClock,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaUser,
  FaCar,
  FaCreditCard,
  FaMoneyBillWave,
  FaTicketAlt,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaCalendarAlt,
} from "react-icons/fa";
import {
  PaymentSuccessAnimation,
  BookingConfirmedAnimation,
  AnimationStyles,
} from "../components/PaymentAnimations";
import PaymentModeSelector from "../components/Payment/PaymentModeSelector";
import PaymentMethodSelector from "../components/Payment/PaymentMethodSelector";
import { getCorporateMember } from "../utils/corporateSession";
import { stripPlusCode } from "../utils/locationMatcher";
import { trackAbandonedBooking, markBookingConverted } from "../utils/abandonedBookingTracker";

// API URL - imported once outside component to prevent dependency issues
const { API_URL } = require("../config/apiConfig");

// Helper to parse via cities from URL query param cleanly without breaking on commas in addresses
const parseViaCities = (viaStr) => {
  if (!viaStr) return [];
  if (typeof viaStr !== "string") {
    if (Array.isArray(viaStr)) {
      return viaStr.map((s) => typeof s === "string" ? s.trim() : (s?.name || s?.address || "")).filter(Boolean);
    }
    return [];
  }
  const str = viaStr.trim();
  if (!str) return [];
  if (str.includes("|")) {
    return str.split("|").map((s) => s.trim()).filter(Boolean);
  }
  // When there is NO pipe delimiter, the entire string is ONE single stop address (do NOT split on commas!)
  return [str];
};

// Utility function to safely decode time parameters
const decodeTimeParam = (timeParam) => {
  if (!timeParam) return null;
  try {
    return decodeURIComponent(timeParam);
  } catch (e) {
    return timeParam;
  }
};

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract paymentOption from query params early to make it available to all handlers
  const queryParams = new URLSearchParams(location.search);
  const paymentOption = queryParams.get("paymentOption") || "full";

  const [isLoading, setIsLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [bookingInfo, setBookingInfo] = useState({});
  const [serviceType, setServiceType] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [packageName, setPackageName] = useState(null);
  const [bookingSettings, setBookingSettings] = useState(null);

  useEffect(() => {
    const fetchBookingSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/booking/settings/public`);
        const data = response.data?.data || (response.data?.success ? response.data.data : response.data);
        if (data) {
          setBookingSettings(data);
        }
      } catch (error) {
        // Silently fail - use defaults
      }
    };
    fetchBookingSettings();
  }, []);

  const [isTopViaOpen, setIsTopViaOpen] = useState(false);
  const [isTimelineViaOpen, setIsTimelineViaOpen] = useState(false);

  // Animation states
  const [showPaymentSuccessAnimation, setShowPaymentSuccessAnimation] =
    useState(false);
  const [showBookingConfirmedAnimation, setShowBookingConfirmedAnimation] =
    useState(false);

  // Wallet states
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletError, setWalletError] = useState(null);

  // Payment mode state
  const [paymentMode, setPaymentMode] = useState(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState({});
  const [paymentModeValidating, setPaymentModeValidating] = useState(false);

  // View details toggle state
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [isMobileBreakupOpen, setIsMobileBreakupOpen] = useState(false);

  // Handle available options from PaymentModeSelector
  const handlePaymentOptionsUpdate = (options) => {
    setPaymentMethodOptions(options);
  };

  const isCorporateBookingFlow =
    new URLSearchParams(location.search).get("corporate") === "1" ||
    sessionStorage.getItem("corporateBookingMode") === "1";

  // Get auth state
  const authState = useSelector((state) => state.auth || {});

  // Get auth from cookie and localStorage as backup
  let storedToken = null;
  try {
    const cookieToken = document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("token="));
    if (cookieToken) {
      storedToken = cookieToken.split("=")[1];
    }
  } catch (e) {}

  const token =
    authState.token ||
    (authState.user && authState.user.token) ||
    storedToken ||
    localStorage.getItem("token") ||
    null;

  // Get corporate token for payment validation
  const corporateToken = localStorage.getItem("corporateToken");
  const authTokenForPayment = corporateToken || token;

  // Extract corporateMember info early (needed for useEffect below)
  const corporateMember = getCorporateMember();
  const corporateMemberId = corporateMember?.id || corporateMember?._id || null;
  const corporateRole = corporateMember?.role || null;

  useEffect(() => {
    // Default to COMPANY mode for corporate bookings, PERSONAL for regular users
    // Corporate staff creating company bookings should bill their company by default
    // Corporate admins MUST use COMPANY mode only
    if (isCorporateBookingFlow || corporateRole === 'admin') {
      setPaymentMode("COMPANY");
    } else {
      setPaymentMode("PERSONAL");
    }
    // Don't auto-set payment method - let user choose
  }, [isCorporateBookingFlow, corporateRole]);

  const storedAuth = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const combinedAuthData = {
    ...authState,
    ...(storedAuth || {}),
  };

  const user = combinedAuthData.user || authState.user || {};

  // For corporate staff, use their member ID; for regular users, use their user ID
  const userId = corporateMemberId ||
    (user && user.id) ||
    (user && user._id) ||
    (storedAuth &&
      storedAuth.user &&
      (storedAuth.user.id || storedAuth.user._id)) ||
    "645f9d9ee88ebd3ddd7ac4c7";

  const getAuthConfig = () => {
    const corporateToken = localStorage.getItem("corporateToken");
    // Use corporate token if available (corporate staff), otherwise use personal token
    const authToken = corporateToken || token;
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : "",
      },
    };
  };

  const config = getAuthConfig();

  // Handle authentication and login redirect
  useEffect(() => {
    const paymentParams = location.search;
    if (paymentParams) {
      sessionStorage.setItem("paymentRedirectParams", paymentParams);
      localStorage.setItem("paymentRedirectParams", paymentParams);
    }

    // Note: corporate=1 in URL doesn't force corporate login anymore
    // Users can choose between personal and company payment modes
    // For regular users, allow guest booking too
    console.log("Payment page loading with auth token:", !!token);
  }, [isCorporateBookingFlow, navigate, location.search, token]);

  // Get wallet balance
  const refreshWalletBalance = async () => {
    let authToken = token;

    // If no token in Redux, try to get from localStorage
    if (!authToken) {
      authToken =
        localStorage.getItem("corporateToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("vendorToken") ||
        localStorage.getItem("driverToken") ||
        localStorage.getItem("adminToken");
    }

    // Check for corporate token for staff members
    const corporateToken = localStorage.getItem("corporateToken");

    // Determine which wallet balance to fetch
    // For corporate users: always use staff wallet endpoint with corporate token
    // For regular users: use personal wallet endpoint
    let tokenToUse = null;
    let endpoint = `${API_URL}/wallet/user/balance`;

    // Check if this is a corporate user (staff or admin)
    const isCorporateUserForWallet = !!(corporateMemberId && corporateToken);

    if (isCorporateUserForWallet) {
      // Corporate users - use staff wallet endpoint
      tokenToUse = corporateToken;
      endpoint = `${API_URL}/wallet/staff/balance`;
    } else if (authToken) {
      // Regular users - use personal wallet
      tokenToUse = authToken;
      endpoint = `${API_URL}/wallet/user/balance`;
    }

    if (!tokenToUse) {
      setWalletBalance(0);
      setWalletError("Please log in to use wallet");
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenToUse}`,
      };

      const response = await axios.get(endpoint, { headers });
      if (response.data?.success) {
        const walletBalance = response.data?.data?.balance || 0;
        setWalletBalance(Number(walletBalance) || 0);
        setWalletError(null);
      } else {
        setWalletBalance(0);
        setWalletError(response.data?.message || "Unable to fetch wallet balance");
      }
    } catch (error) {
      console.error("Wallet fetch error:", error);
      setWalletBalance(0);
      setWalletError("Unable to fetch wallet balance");
    }
  };

  // Fetch wallet balance on page load and when payment method/mode changes
  useEffect(() => {
    const corporateToken = localStorage.getItem("corporateToken");
    if (token || isCorporateBookingFlow || corporateToken) {
      refreshWalletBalance();
    }
  }, [token, isCorporateBookingFlow, paymentMode, corporateMemberId]);

  useEffect(() => {
    if (selectedPaymentMethod === "WALLET") {
      refreshWalletBalance();
    }
  }, [selectedPaymentMethod]);

  // Initialize booking info from URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const fromCity = queryParams.get("from");
    const toCity = queryParams.get("to");
    const viaCity = queryParams.get("via")
      ? decodeURIComponent(queryParams.get("via"))
      : "";
    const cabId = queryParams.get("cabId");
    const bookingType = queryParams.get("tripType") || queryParams.get("type");
    const name = queryParams.get("name");
    const mobile = queryParams.get("mobile");
    const email = queryParams.get("email");
    const gender = queryParams.get("gender");
    const passengerId = queryParams.get("passengerId");
    const bookingFor = queryParams.get("bookingFor") || "user";
    const pickupAddress = queryParams.get("pickupAddress")
      ? stripPlusCode(decodeURIComponent(queryParams.get("pickupAddress")))
      : "";
    const dropAddress = queryParams.get("dropAddress")
      ? stripPlusCode(decodeURIComponent(queryParams.get("dropAddress")))
      : "";
    const amount = queryParams.get("amount");
    const travelDate = queryParams.get("date");
    const travelTime = queryParams.get("time");
    const distance = queryParams.get("distance");
    const serviceTypeParam = queryParams.get("service");
    const airportParam = queryParams.get("airport");
    const directionParam = queryParams.get("direction");
    const returnDateFromUrl = queryParams.get("returnDate");
    const returnTimeFromUrl = queryParams.get("returnTime");
    const tripTypeFromUrl = queryParams.get("tripType") || (returnDateFromUrl ? "roundTrip" : "oneWay");

    // Read coupon from URL params
    const couponCodeFromUrl = queryParams.get("couponCode");
    const couponDiscountFromUrl = queryParams.get("couponDiscount");
    if (couponCodeFromUrl && couponDiscountFromUrl) {
      setPromoCode(couponCodeFromUrl);
      setPromoDiscount(parseFloat(couponDiscountFromUrl) || 0);
      setPromoApplied(true);
    }

    const allPackageParams = queryParams.getAll("package");
    let packageNameFromUrl = null;
    if (allPackageParams.length > 0) {
      let bestPackage = allPackageParams[0];
      for (const pkg of allPackageParams) {
        try {
          const decoded = decodeURIComponent(pkg);
          const currentBestDecoded = decodeURIComponent(bestPackage);
          if (
            decoded.includes(" ") &&
            decoded.includes("/") &&
            decoded.length < currentBestDecoded.length
          ) {
            bestPackage = pkg;
          }
        } catch (e) {}
      }
      packageNameFromUrl = bestPackage;
    }

    setPackageName(packageNameFromUrl);
    setServiceType(serviceTypeParam);

    const isRentalOrAirport =
      serviceTypeParam === "rental" || serviceTypeParam === "airport";

    if (
      !fromCity ||
      (!toCity && !isRentalOrAirport) ||
      !cabId ||
      !name ||
      !mobile ||
      !gender ||
      !amount
    ) {
      if (!toast.isActive("missing-booking-info")) {
        toast.error("Missing required booking information", { toastId: "missing-booking-info" });
      }
      navigate("/");
      return;
    }

    const cabNameFromUrl = decodeURIComponent(queryParams.get("cabName") || "");
    const imageUrl = queryParams.get("imageUrl")
      ? decodeURIComponent(queryParams.get("imageUrl"))
      : "";

    const basePrice = parseFloat(amount) || 0;

    // Get coupon discount from URL (if already applied at customer-details page)
    const couponDiscountAmount = parseFloat(couponDiscountFromUrl) || 0;

    // Get add-ons cost from URL
    const addOnsCostFromUrl = parseFloat(queryParams.get("addOnsCost")) || 0;

    // Parse selectedAddOns from URL (JSON string)
    let selectedAddOnsFromUrl = [];
    try {
      const addOnsParam = queryParams.get("selectedAddOns");
      if (addOnsParam) {
        selectedAddOnsFromUrl = JSON.parse(decodeURIComponent(addOnsParam));
        if (!Array.isArray(selectedAddOnsFromUrl)) {
          selectedAddOnsFromUrl = [];
        }
      }
    } catch (error) {
      console.error("Error parsing selectedAddOns from URL:", error);
      selectedAddOnsFromUrl = [];
    }

    // Calculate in the correct order:
    // 1. Apply coupon discount to base fare FIRST
    const discountedBaseFare = Math.max(0, basePrice - couponDiscountAmount);

    // 2. Calculate tax on the discounted fare (18% GST)
    const taxRate = 0.18;
    const taxAmount = Math.round(discountedBaseFare * taxRate) || 0;

    // 3. Add add-ons to get final total
    const totalAmount = discountedBaseFare + taxAmount + addOnsCostFromUrl;

    setBookingInfo({
      cabId,
      cabName: cabNameFromUrl,
      cabImage: imageUrl,
      fromCity,
      toCity: toCity || fromCity,
      viaCity,
      name,
      mobile,
      email,
      gender,
      passengerId,
      bookingFor,
      pickupAddress,
      dropAddress,
      pickupDate: travelDate,
      travelTime: decodeTimeParam(travelTime),
      pickupTime: decodeTimeParam(travelTime),
      distance: parseFloat(distance) || 0,
      basePrice: basePrice,
      amount: basePrice,
      discountedBaseFare: discountedBaseFare,
      taxAmount: taxAmount,
      addOnsCost: addOnsCostFromUrl,
      selectedAddOns: selectedAddOnsFromUrl,
      totalAmount: totalAmount,
      bookingType,
      tripType: tripTypeFromUrl,
      returnDate: returnDateFromUrl || null,
      returnTime: returnTimeFromUrl ? decodeTimeParam(returnTimeFromUrl) : null,
      airportCode: airportParam,
      transferDirection: directionParam,
    });

    // Track lead on reaching checkout/payment page
    try {
      trackAbandonedBooking({
        name,
        phone: mobile,
        email,
        pickupLocation: { address: pickupAddress || fromCity || "" },
        dropLocation: { address: dropAddress || toCity || "" },
        tripType: bookingType || "one-way",
        pickupDate: travelDate || "",
        pickupTime: decodeTimeParam(travelTime) || "",
        selectedCab: {
          cabId,
          cabName: cabNameFromUrl,
          price: totalAmount,
        },
        estimatedPrice: totalAmount,
        distance: parseFloat(distance) || 0,
        exitPage: "/booking/payment",
      });
    } catch (trackErr) {
      console.warn("Lead tracking silent error:", trackErr);
    }

    setIsLoading(false);
  }, [location.search, navigate]);

  // Apply promo code
  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }

    const code = promoCode.trim().toUpperCase();

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("corporateToken");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.post(
        `${API_URL}/coupons/validate`,
        {
          code,
          bookingAmount: bookingInfo.amount,
          cabType: bookingInfo.cabType || "all"
        },
        config
      );

      if (response.data.success || response.data.coupon) {
        const validatedCoupon = response.data.coupon || response.data.data;
        setPromoDiscount(validatedCoupon.discountAmount);
        setPromoApplied(true);
        setPromoCode("");
        toast.success(`Coupon ${validatedCoupon.code} applied successfully!`);
      } else {
        toast.error(response.data.message || "Invalid coupon code");
      }
    } catch (error) {
      console.log("Coupon validation error:", error);
      const message = error.response?.data?.message || "Invalid coupon code";
      toast.error(`For this ride, this coupon is not valid: ${message}`);
    }
  };

  const removePromoCode = () => {
    setPromoApplied(false);
    setPromoDiscount(0);
    setPromoCode("");
  };

  // Handle payment mode change
  const handlePaymentModeChange = (newMode) => {
    setPaymentMode(newMode);

    // Reset selected method if it's incompatible with the new mode
    if (newMode === "COMPANY" && selectedPaymentMethod !== "COMPANY") {
      // When switching to COMPANY mode, only COMPANY payment method is available
      setSelectedPaymentMethod("COMPANY");
    } else if (newMode === "PERSONAL" && selectedPaymentMethod === "COMPANY") {
      // When switching to PERSONAL mode, COMPANY method is not valid
      setSelectedPaymentMethod(null);
    }
    // For PERSONAL to PERSONAL or COMPANY to COMPANY, keep the current selection
  };

  // Handle payment method change
  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
  };

  // Fetch available coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        // Use corporate token for corporate users, otherwise use regular token
        const corporateToken = localStorage.getItem("corporateToken");
        const authToken = corporateToken || token;
        
        const couponConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: authToken ? `Bearer ${authToken}` : "",
          },
        };
        const response = await axios.get(`${API_URL}/coupons/available`, couponConfig);
        const couponData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        setAvailableCoupons(couponData);
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    };
    fetchCoupons();
  }, [token]);

  // Calculate dynamic partial pay details
  const advancePercentage = bookingSettings && typeof bookingSettings.advancePercentage === 'number'
    ? bookingSettings.advancePercentage
    : 20; // fallback to 20%
  const totalAmount = bookingInfo.totalAmount || 0;
  const partialAmount = Math.round(totalAmount * (advancePercentage / 100));
  const remainingAmount = totalAmount - partialAmount;

  // When partial payment + COD: driver collects full amount, so totalDue = totalAmount
  const isCodWithPartial = paymentOption === "partial" && selectedPaymentMethod === "COD";
  const totalDue = (paymentOption === "partial" && !isCodWithPartial) ? partialAmount : totalAmount;

  // Initiate payment
  const initiatePayment = async () => {
    // Check if user is corporate or normal
    const isCorporateUser = !!(corporateMemberId || isCorporateBookingFlow);
    
    // Validate payment mode for corporate users only
    if (isCorporateUser && !paymentMode) {
      toast.error("Please select a payment mode");
      return;
    }

    if (!selectedPaymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    // Validate payment mode and method combination (corporate only)
    if (isCorporateUser) {
      if (paymentMode === "COMPANY" && selectedPaymentMethod !== "COMPANY") {
        toast.error("Company payment mode requires COMPANY payment method");
        return;
      }

      if (paymentMode === "PERSONAL" && selectedPaymentMethod === "COMPANY") {
        toast.error("Cannot use COMPANY payment method with PERSONAL mode");
        return;
      }

      // Validate corporate account balance for COMPANY payment mode
      if (paymentMode === "COMPANY") {
        const corporateBalance = paymentMethodOptions?.company?.availableBalance;
        if (corporateBalance === undefined || corporateBalance === null) {
          toast.error("Your company account doesn't have sufficient balance to book this ride. Please contact your company admin to top up the wallet.");
          return;
        }
        const requiredCompanyAmount = (paymentOption === "partial" && selectedPaymentMethod !== "COD")
          ? totalDue
          : bookingInfo.totalAmount;
        if (corporateBalance < requiredCompanyAmount) {
          const shortfall = (requiredCompanyAmount - corporateBalance).toLocaleString("en-IN");
          const needed = requiredCompanyAmount.toLocaleString("en-IN");
          const available = corporateBalance.toLocaleString("en-IN");
          toast.error(
            `Your company account doesn't have enough balance. You need ₹${needed} but only ₹${available} is available (₹${shortfall} short). Please contact your company admin to top up the wallet.`
          );
          return;
        }
      }
    }

    // Validate wallet for wallet payments
    // Use the balance from availableOptions (fetched from backend validation) if available
    const actualWalletBalance = paymentMethodOptions?.personal?.methods?.wallet?.balance ?? walletBalance;
    const requiredWalletAmount = (paymentOption === "partial" && selectedPaymentMethod !== "COD")
      ? totalDue
      : (bookingInfo.totalAmount || 0);
    if (
      selectedPaymentMethod === "WALLET" &&
      requiredWalletAmount > actualWalletBalance
    ) {
      toast.error(
        `Insufficient wallet balance. You need ₹${(requiredWalletAmount - actualWalletBalance).toLocaleString("en-IN")} more.`
      );
      return;
    }

    setProcessingPayment(true);

    try {
      // Get correct auth config for this request
      const requestConfig = getAuthConfig();

      // Map payment methods to API expected values
      const paymentMethodMap = {
        "COD": "COD",
        "ONLINE": "ONLINE",
        "WALLET": "WALLET",
        "COMPANY": "COMPANY",
      };

      // Create booking first
      const isCorporateUserForPayload = !!(corporateMemberId || isCorporateBookingFlow);
      const bookingPayload = {
        pickupLocation: bookingInfo.pickupAddress || bookingInfo.fromCity,
        dropLocation: bookingInfo.dropAddress || bookingInfo.toCity,
        from: bookingInfo.fromCity,
        to: bookingInfo.toCity,
        pickupAddress: bookingInfo.pickupAddress,
        dropAddress: bookingInfo.dropAddress,
        cabTypeId: bookingInfo.cabId,
        passengerDetails: {
          name: bookingInfo.name,
          email: bookingInfo.email,
          phone: bookingInfo.mobile,
          gender: bookingInfo.gender,
          id: bookingInfo.passengerId || bookingInfo.userId || null,
        },
        journeyType: bookingInfo.bookingType || (bookingInfo.returnDate ? "roundTrip" : "oneWay"),
        tripType: bookingInfo.tripType || (bookingInfo.returnDate ? "roundTrip" : "oneWay"),
        pickupDate: bookingInfo.pickupDate,
        pickupTime: bookingInfo.pickupTime,
        returnDate: bookingInfo.returnDate || queryParams.get("returnDate") || undefined,
        returnTime: bookingInfo.returnTime || (queryParams.get("returnTime") ? decodeTimeParam(queryParams.get("returnTime")) : undefined),
        distance: bookingInfo.distance,
        baseAmount: bookingInfo.basePrice || bookingInfo.amount || 0,
        taxAmount: bookingInfo.taxAmount || 0,
        addOnsCost: bookingInfo.addOnsCost || 0,
        selectedAddOns: bookingInfo.selectedAddOns || [],
        totalAmount: bookingInfo.totalAmount,
        bookingFor: bookingInfo.bookingFor || "user",
        paymentMode: isCorporateUserForPayload ? paymentMode : "PERSONAL",
        paymentMethod: paymentMethodMap[selectedPaymentMethod] || selectedPaymentMethod,
        couponCode: promoApplied ? promoCode : undefined,
        couponDiscountAmount: promoDiscount,
        serviceType: serviceType,
        paymentOption: (paymentOption === "partial" && selectedPaymentMethod === "COD") ? "full" : paymentOption,
        advancePaymentAmount: (paymentOption === "partial" && selectedPaymentMethod !== "COD") ? totalDue : undefined,
        advancePercentage: (paymentOption === "partial" && selectedPaymentMethod !== "COD") ? advancePercentage : undefined,
        viaCities: parseViaCities(bookingInfo.viaCity),
      };

      // Add corporate ID if company payment mode
      if (paymentMode === "COMPANY") {
        bookingPayload.corporateAccountId = corporateMember?.corporateAccountId || combinedAuthData?.corporateAccountId || new URLSearchParams(location.search).get("corporateAccountId");
      }

      // Add corporateMemberId when admin is booking for staff (from passenger details)
      if (isCorporateUserForPayload && bookingInfo.passengerId) {
        bookingPayload.corporateMemberId = bookingInfo.passengerId;
        console.log(`[PaymentPage] Admin booking for staff member: ${bookingInfo.passengerId}`);
      }

      // Add staff object when booking for staff
      if (bookingInfo.bookingFor === 'staff' && bookingInfo.passengerId) {
        bookingPayload.staff = {
          staffId: bookingInfo.passengerId,
          name: bookingInfo.name,
          email: bookingInfo.email,
          phone: bookingInfo.mobile,
          gender: bookingInfo.gender || 'Male'
        };
        console.log(`[PaymentPage] Adding staff object to payload:`, bookingPayload.staff);
      }

      if (serviceType === "rental" && packageName) {
        bookingPayload.packageName = decodeURIComponent(packageName);
      }

      if (serviceType === "airport") {
        if (bookingInfo.airportCode) {
          bookingPayload.airportCode = bookingInfo.airportCode;
        }
        if (bookingInfo.transferDirection) {
          bookingPayload.transferDirection = bookingInfo.transferDirection;
        }
      }

      let bookingData;
      try {
        const bookingResponse = await axios.post(
          `${API_URL}/booking`,
          bookingPayload,
          requestConfig
        );
        bookingData = bookingResponse.data?.data || bookingResponse.data;
      } catch (error) {
        console.error("Booking creation error:", error);
        // Only show the specific error message from the backend
        const errorMessage = error.response?.data?.message || error.message || "Booking failed";
        toast.error(errorMessage);
        setProcessingPayment(false);
        return;
      }

      const tempBookingId = bookingData.bookingId || bookingData.id || bookingData._id;
      if (!tempBookingId) {
        toast.error("Invalid booking data received. Please try again.");
        setProcessingPayment(false);
        return;
      }

      if (selectedPaymentMethod === "COD") {
        // Cash on Delivery flow
        try {
          const targetId = tempBookingId;
          await axios.post(
            `${API_URL}/booking/payment-update/custom`,
            {
              bookingId: targetId,
              paymentStatus: "pending",
              paymentMethod: "COD",
            },
            requestConfig
          );

          markBookingConverted();
          setShowPaymentSuccessAnimation(true);
          setTimeout(() => {
            setShowPaymentSuccessAnimation(false);
            navigate(`/booking/${targetId}`);
          }, 3000);
        } catch (error) {
          toast.error("Failed to process cash on delivery booking");
          setProcessingPayment(false);
        }
      } else if (selectedPaymentMethod === "WALLET") {
        // Wallet payment flow
        try {
          await axios.post(
            `${API_URL}/payment/process-wallet-payment`,
            {
              amount: totalDue,
              bookingId: tempBookingId,
            },
            requestConfig
          );

          markBookingConverted();
          setShowPaymentSuccessAnimation(true);
          setTimeout(() => {
            setShowPaymentSuccessAnimation(false);
            navigate(`/booking/${tempBookingId}`);
          }, 3000);
        } catch (error) {
          toast.error(error.response?.data?.message || "Wallet payment failed");
          setProcessingPayment(false);
        }
      } else if (selectedPaymentMethod === "COMPANY") {
        // Company/Corporate payment flow - no additional payment processing needed
        // Booking is already created with paymentMethod: "COMPANY"
        markBookingConverted();
        setShowPaymentSuccessAnimation(true);
        setTimeout(() => {
          setShowPaymentSuccessAnimation(false);
          navigate(`/booking/${tempBookingId}`);
        }, 3000);
      } else if (selectedPaymentMethod === "ONLINE") {
        // Razorpay payment flow
        if (!window.Razorpay) {
          toast.error("Razorpay SDK failed to load. Please refresh the page.");
          setProcessingPayment(false);
          return;
        }

        try {
          const createOrderUrl = paymentOption === "partial"
            ? `${API_URL}/booking/create-advance-order`
            : `${API_URL}/payment/create-order`;

          const orderResponse = await axios.post(
            createOrderUrl,
            {
              amount: totalDue,
              currency: "INR",
              bookingId: tempBookingId,
            },
            requestConfig
          );

          // Standardize order details for Razorpay options
          let orderId, finalPayAmount, orderKey;
          if (paymentOption === "partial") {
            const dataObj = orderResponse.data?.data || orderResponse.data;
            orderId = dataObj?.orderId;
            finalPayAmount = dataObj?.amount || totalDue;
            orderKey = dataObj?.key;
          } else {
            const orderObj = orderResponse.data?.order || orderResponse.data;
            orderId = orderObj?.id;
            finalPayAmount = totalDue;
            orderKey = orderResponse.data?.key;
          }

          const options = {
            key: orderKey || process.env.REACT_APP_RAZORPAY_KEY || process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_PnSgiqE2elzEYx",
            amount: finalPayAmount * 100,
            currency: "INR",
            order_id: orderId,
            handler: async (response) => {
              try {
                const verifyUrl = paymentOption === "partial"
                  ? `${API_URL}/booking/verify-advance-payment`
                  : `${API_URL}/payment/verify-payment`;

                const verifyResponse = await axios.post(
                  verifyUrl,
                  {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    bookingId: tempBookingId,
                  },
                  requestConfig
                );

                markBookingConverted();
                setShowPaymentSuccessAnimation(true);
                setTimeout(() => {
                  setShowPaymentSuccessAnimation(false);
                  navigate(`/booking/${tempBookingId}`);
                }, 3000);
              } catch (error) {
                toast.error("Payment verification failed");
                setProcessingPayment(false);
              }
            },
            prefill: {
              name: bookingInfo.name,
              email: bookingInfo.email || "",
              contact: bookingInfo.mobile,
            },
            theme: { color: "#3B82F6" },
            modal: {
              ondismiss: function () {
                setProcessingPayment(false);
                toast.info("Payment cancelled");
              },
            },
          };

          const razorpayInstance = new window.Razorpay(options);
          razorpayInstance.open();
        } catch (error) {
          toast.error("Failed to initiate payment");
          setProcessingPayment(false);
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to process payment");
      setProcessingPayment(false);
    }
  };

  const viaCitiesList = parseViaCities(bookingInfo.viaCity);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="bg-white min-h-screen relative">
      {/* MOBILE HEADER (MakeMyTrip Style) - FIXED to viewport top */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-md">
        <div className="flex items-center px-4 py-1.5 gap-2">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="text-white hover:text-gray-200 transition-colors p-1 flex-shrink-0"
            aria-label="Go Back"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <p className="font-bold !text-sm leading-tight truncate">
              Payment Details
            </p>
            <p className="text-[11px] text-blue-200 font-semibold mt-0.5 truncate">
              {serviceType === "rental" ? (
                `Local Rental in ${bookingInfo.fromCity?.split(",")[0] || bookingInfo.fromCity}`
              ) : (
                `${bookingInfo.fromCity?.split(",")[0] || bookingInfo.fromCity} to ${(bookingInfo.toCity || bookingInfo.fromCity)?.split(",")[0]}`
              )}
              {bookingInfo.pickupDate ? ` • ${formatDateDDMMYYYY(bookingInfo.pickupDate)}` : ""}
              {bookingInfo.pickupTime ? ` • ${convertTo12HourFormat(decodeURIComponent(bookingInfo.pickupTime))}` : ""}
            </p>
          </div>
        </div>
      </div>

      {showPaymentSuccessAnimation && (
        <PaymentSuccessAnimation
          paymentMethod={selectedPaymentMethod?.toUpperCase() === "COD" ? "cod" : "online"}
          onComplete={() => {
            setShowPaymentSuccessAnimation(false);
            setShowBookingConfirmedAnimation(true);
            setTimeout(() => {
              setShowBookingConfirmedAnimation(false);
            }, 3000);
          }}
        />
      )}

      {showBookingConfirmedAnimation && (
        <BookingConfirmedAnimation
          bookingData={{
            id: bookingInfo._id || bookingInfo.id,
            fromCity: bookingInfo.fromCity,
            toCity: bookingInfo.toCity,
            cabName: bookingInfo.cabName,
            amount: bookingInfo.totalAmount,
            date: bookingInfo.pickupDate,
            time: bookingInfo.pickupTime,
          }}
          onComplete={() => {
            setShowBookingConfirmedAnimation(false);
          }}
        />
      )}

      <AnimationStyles />

      <div className="max-w-[1190px] mx-auto pt-[48px] md:pt-4 pb-16 md:pb-8 px-4 lg:px-8">

        {/* Desktop Header */}
        <div className="hidden md:flex items-center gap-3 mb-6 bg-blue-600 rounded-md py-3 px-6 text-white">
          <button 
            type="button"
            onClick={() => navigate(-1)} 
            className="hover:bg-blue-700/50 p-1.5 rounded-full transition-colors flex items-center justify-center flex-shrink-0"
            aria-label="Go Back"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold">Payment</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT SIDE */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Details - CustomerDetails Style */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              {/* Header - Summary */}
              <div className="bg-gray-100 px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 mb-2">
                      {serviceType === "rental"
                        ? "Local Rental"
                        : serviceType === "airport"
                        ? "Airport Transfer"
                        : bookingInfo.bookingType === "roundTrip"
                        ? "Round Trip"
                        : "Outstation Oneway Trip"}
                    </p>

                    {/* Route */}
                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start md:items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 md:mt-0 flex-shrink-0"></span>
                          <p className="text-[14px] md:text-[16px] font-semibold text-gray-900 leading-tight break-words">
                            {bookingInfo.fromCity}
                          </p>
                        </div>
                      </div>

                      <div className="hidden md:flex items-center gap-2 flex-shrink-0 mx-1">
                        <span className="w-10 h-[2px] bg-gray-400"></span>
                        <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start md:items-center gap-2 md:justify-end">
                          <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 md:mt-0 flex-shrink-0 md:hidden"></span>
                          <p className="text-[14px] md:text-[16px] font-semibold text-gray-900 leading-tight break-words md:text-right">
                            {bookingInfo.toCity || bookingInfo.fromCity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Via Cities Dropdown */}
                    {viaCitiesList.length > 0 && (
                      <div className="mt-3 pt-2.5 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => setIsTopViaOpen((prev) => !prev)}
                          className="inline-flex items-center gap-2.5 py-1 px-2.5 -ml-2 rounded-lg hover:bg-orange-50/70 transition-colors text-left group cursor-pointer"
                        >
                          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 uppercase tracking-wider">
                            <FaMapMarkerAlt className="text-[#f26a21] text-xs" />
                            <span>Via Stops ({viaCitiesList.length})</span>
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 group-hover:text-[#f26a21] font-semibold transition-colors">
                            <span>{isTopViaOpen ? "Hide" : "View"}</span>
                            {isTopViaOpen ? (
                              <FaChevronUp className="text-[10px]" />
                            ) : (
                              <FaChevronDown className="text-[10px]" />
                            )}
                          </div>
                        </button>

                        {isTopViaOpen && (
                          <div className="mt-2.5 flex flex-wrap items-center gap-2">
                            {viaCitiesList.map((city, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-gray-800 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-xs">
                                  <span className="w-4 h-4 rounded-full bg-[#f26a21] text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                                    {idx + 1}
                                  </span>
                                  <span className="break-words">{city}</span>
                                </span>
                                {idx < viaCitiesList.length - 1 && (
                                  <span className="text-gray-400 font-bold text-sm select-none">→</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Date & Time */}
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-700">
                      <div className="flex items-center gap-1">
                        <FaClock className="text-gray-500" />
                        <span>{bookingInfo.pickupTime || "Time not set"}</span>
                      </div>

                      <div className="flex items-center gap-1">
                        <FaCalendarAlt className="text-gray-500" />
                        <span>{bookingInfo.pickupDate ? formatDateDDMMYYYY(bookingInfo.pickupDate) : "Date not set"}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowTripDetails(!showTripDetails)}
                    className="flex items-center gap-1 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors ml-4 flex-shrink-0"
                  >
                    {showTripDetails ? "HIDE" : "DETAILS"} {showTripDetails ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
              </div>

              {/* Car Info Bar */}
              <div className="flex justify-between items-center px-6 py-1 gap-3 border-t border-gray-100">
                <div className="leading-none">
                  <h3 className="text-lg font-bold text-gray-900 uppercase">
                    {bookingInfo.cabName}
                  </h3>
                  <p className="text-sm text-gray-500 leading-snug">Or equivalent</p>
                </div>

                {bookingInfo.cabImage && (
                  <div className="flex flex-col items-center bg-white rounded-lg shadow-sm border border-gray-200 p-1 min-w-[100px]">
                    <img
                      src={bookingInfo.cabImage}
                      alt={bookingInfo.cabName}
                      className="w-24 h-16 object-contain"
                    />
                    <span className="bg-orange-100 text-orange-600 text-[11px] font-bold px-3 py-0.5 rounded-full mt-1 leading-tight uppercase">
                      Petrol
                    </span>
                  </div>
                )}
              </div>
              
              {/* Expanded Details */}
              {showTripDetails && (
                <div className="border-t border-gray-100">
                  {/* Journey Timeline */}
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Timeline Line with Icons */}
                      <div className="flex flex-col items-center">
                        {/* Pickup Icon */}
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <FaMapMarkerAlt className="text-blue-600" />
                        </div>
                        {/* Line to via or drop */}
                        <div className="w-0.5 flex-1 bg-gray-200 my-1 min-h-[20px]"></div>
                        {/* Via Icon (if exists) */}
                        {bookingInfo.viaCity && (
                          <>
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 my-1">
                              <span className="text-orange-600 text-xs font-bold">via</span>
                            </div>
                            <div className="w-0.5 flex-1 bg-gray-200 my-1 min-h-[20px]"></div>
                          </>
                        )}
                        {/* Drop Icon */}
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                          <FaMapMarkerAlt className="text-green-600" />
                        </div>
                      </div>
                      
                      {/* Location Details */}
                      <div className="flex-1 flex flex-col justify-between py-1">
                        {/* Pickup */}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Pickup from: {bookingInfo.pickupDate && bookingInfo.pickupTime
                            ? `${formatDateDDMMYYYY(bookingInfo.pickupDate)}, ${convertTo12HourFormat(bookingInfo.pickupTime)}`
                            : "Not set"}</p>
                          <p className="text-sm text-gray-600 mt-1">{bookingInfo.pickupAddress || bookingInfo.fromCity}</p>
                        </div>
                        
                        {/* Via Stop */}
                        {viaCitiesList.length > 0 && (
                          <div className="my-2">
                            <div className="bg-orange-50/80 rounded-xl p-3 border border-orange-200/80 w-full">
                              <button
                                type="button"
                                onClick={() => setIsTimelineViaOpen((prev) => !prev)}
                                className="flex items-center justify-between w-full text-left cursor-pointer group"
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-bold text-orange-800">
                                    Via Stops ({viaCitiesList.length})
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-orange-700 font-semibold group-hover:text-orange-900 transition-colors">
                                  <span>{isTimelineViaOpen ? "Hide" : "View"}</span>
                                  {isTimelineViaOpen ? (
                                    <FaChevronUp className="text-[10px]" />
                                  ) : (
                                    <FaChevronDown className="text-[10px]" />
                                  )}
                                </div>
                              </button>

                              {isTimelineViaOpen && (
                                <div className="mt-2.5 pt-2 border-t border-orange-200/60 flex flex-col gap-2">
                                  {viaCitiesList.map((city, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-white/90 border border-orange-100 rounded-lg px-2.5 py-1.5 shadow-xs">
                                      <span className="w-5 h-5 rounded-full bg-[#f26a21] text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                                        {idx + 1}
                                      </span>
                                      <span className="text-xs font-semibold text-gray-800 break-words">{city}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Drop */}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Drop off: {bookingInfo.toCity || bookingInfo.fromCity}</p>
                          <p className="text-sm text-gray-600 mt-1">{bookingInfo.dropAddress || bookingInfo.toCity || bookingInfo.fromCity}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Passenger Details */}
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-gray-700">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FaUser className="text-gray-400 flex-shrink-0" />
                        <span className="font-semibold truncate max-w-[120px]">{bookingInfo.name}</span>
                      </div>
                      <span className="text-gray-300 hidden sm:inline">|</span>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FaEnvelope className="text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[180px]">{bookingInfo.email || "N/A"}</span>
                      </div>
                      <span className="text-gray-300 hidden sm:inline">|</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <FaPhone className="text-gray-400 flex-shrink-0" />
                        <span className="whitespace-nowrap font-medium">+91 {bookingInfo.mobile}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Promo Code */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl shadow-md p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-4">
                <FaTicketAlt className="text-blue-600 text-lg" />
                <h3 className="font-semibold text-gray-800">Apply Promo Code</h3>
              </div>

              <div className="flex gap-1.5 sm:gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter Promo Code"
                  disabled={promoApplied}
                  className="flex-1 w-full min-w-0 border-2 border-blue-200 rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium"
                />

                {!promoApplied ? (
                  <button
                    onClick={applyPromoCode}
                    className="bg-blue-600 text-white px-3 sm:px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 text-sm active:scale-95 flex-shrink-0"
                  >
                    Apply
                  </button>
                ) : (
                  <button
                    onClick={removePromoCode}
                    className="bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-300 transition-all duration-200 active:scale-95 flex-shrink-0"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {promoApplied && (
                <p className="text-sm text-green-600 mt-3 font-medium">
                  ✓ Promo code applied! You saved ₹{promoDiscount}
                </p>
              )}

              {availableCoupons.length > 0 && !promoApplied && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Available Coupons:</p>
                  {availableCoupons
                    .filter(c => bookingInfo.amount >= (c.minimumBookingAmount || 0))
                    .map((coupon, index) => (
                      <div 
                        key={coupon._id || index}
                        className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-blue-50 transition-colors"
                        onClick={() => {
                          if (bookingInfo.amount < (coupon.minimumBookingAmount || 0)) {
                            toast.error(`For this ride, this coupon is not valid: Minimum booking amount of ₹${coupon.minimumBookingAmount} is required`);
                            return;
                          }
                          setPromoCode(coupon.code);
                          setPromoApplied(true);
                          const discount = coupon.discountType === 'percentage' 
                            ? Math.round(bookingInfo.amount * (coupon.discountValue / 100))
                            : coupon.discountValue;
                          setPromoDiscount(discount);
                          toast.success(`Coupon ${coupon.code} applied!`);
                        }}
                      >
                        <input 
                          type="radio" 
                          name="coupon" 
                          className="mt-1 accent-blue-600" 
                        />
                        <div>
                          <div className="inline-flex items-center gap-2 border border-dashed border-blue-600 text-blue-600 px-3 py-1 rounded-md font-semibold text-sm">
                            <FaTicketAlt />
                            {coupon.code}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {coupon.description || `${coupon.discountType === 'percentage' ? coupon.discountValue + '%' : '₹' + coupon.discountValue} off`}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Payment Selection Card */}
            {bookingInfo.fromCity && (
              <>
                <div className="bg-white rounded-xl shadow overflow-hidden">
                  {/* Normal Users - Simple Payment Methods UI */}
                  {!corporateMemberId && !isCorporateBookingFlow && (
                    <div className="p-5">
                      <h3 className="font-semibold text-base text-gray-800 mb-4">
                        Select Payment Method
                      </h3>
                      
                      <div className="space-y-3">
                        {/* Razorpay Option */}
                        <label
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedPaymentMethod === "ONLINE"
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-blue-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="ONLINE"
                            checked={selectedPaymentMethod === "ONLINE"}
                            onChange={(e) => handlePaymentMethodChange(e.target.value)}
                            className="mt-1 accent-blue-600"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                              </svg>
                              <span className="font-semibold text-gray-800">Razorpay</span>
                            </div>
                            <p className="text-sm text-gray-500 mb-2">Pay securely with cards, UPI, Wallets & more</p>
                            <ul className="text-xs text-gray-400 space-y-1 ml-4">
                              <li>• All payment methods accepted in one place</li>
                              <li>• Fast and secure payment process</li>
                              <li>• No additional charges</li>
                            </ul>
                          </div>
                        </label>

                        {/* Cash on Delivery */}
                        <label
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                            selectedPaymentMethod === "COD"
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-green-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="COD"
                            checked={selectedPaymentMethod === "COD"}
                            onChange={(e) => handlePaymentMethodChange(e.target.value)}
                            className="mt-1 accent-green-600 cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FaMoneyBillWave className="text-green-600 text-lg" />
                              <span className="font-semibold text-gray-800">Cash on Delivery</span>
                            </div>
                            <p className="text-sm text-gray-500">Pay directly to driver when you reach destination</p>
                            {paymentOption === "partial" && (
                              <p className="text-xs text-orange-600 mt-2 font-medium bg-orange-50 px-2 py-1 rounded">
                                💡 COD requires full payment — driver collects ₹{totalAmount.toLocaleString("en-IN")} on arrival
                              </p>
                            )}
                          </div>
                        </label>

                        {/* My Wallet */}
                        <label
                          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedPaymentMethod === "WALLET"
                              ? "border-purple-500 bg-purple-50"
                              : "border-gray-200 hover:border-purple-300"
                          } ${walletBalance <= 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="WALLET"
                            checked={selectedPaymentMethod === "WALLET"}
                            onChange={(e) => handlePaymentMethodChange(e.target.value)}
                            disabled={walletBalance <= 0}
                            className="mt-1 accent-purple-600"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 mb-1">
                                <FaWallet className="text-purple-600 text-lg" />
                                <span className="font-semibold text-gray-800">My Wallet</span>
                              </div>
                              <span className="font-semibold text-purple-600">₹{walletBalance.toLocaleString("en-IN")}</span>
                            </div>
                            <p className="text-sm text-gray-500">Pay using your wallet balance</p>
                            {walletBalance < totalDue && walletBalance > 0 && (
                              <p className="text-xs text-orange-500 mt-1">⚠️ Insufficient balance</p>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Corporate Users - Payment Mode + Method Selector */}
                  {(corporateMemberId || isCorporateBookingFlow) && (
                    <>
                      {/* Payment Modes Section */}
                      <div className="border-b p-5">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                            1
                          </div>
                          <p className="text-sm text-gray-700 font-semibold">
                            Choose your payment mode
                          </p>
                        </div>
                        <PaymentModeSelector
                          selectedMode={paymentMode}
                          onModeChange={handlePaymentModeChange}
                          onOptionsChange={handlePaymentOptionsUpdate}
                          userId={userId}
                          corporateAccountId={
                            isCorporateBookingFlow
                              ? corporateMember?.corporateAccountId
                              : null
                          }
                          corporateMemberId={corporateMemberId}
                          corporateRole={corporateRole}
                          fare={totalDue || bookingInfo.totalAmount}
                          isLoading={paymentModeValidating || processingPayment}
                          token={authTokenForPayment}
                        />
                      </div>

                      {/* Payment Method Selector */}
                      {paymentMode && (
                        <div className="p-5 bg-gradient-to-br from-gray-50 to-white">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                              2
                            </div>
                            <p className="text-sm text-gray-700 font-semibold">
                              Select your payment method
                            </p>
                          </div>
                          <PaymentMethodSelector
                            paymentMode={paymentMode}
                            selectedMethod={selectedPaymentMethod}
                            onMethodChange={handlePaymentMethodChange}
                            walletBalance={walletBalance}
                            fare={totalDue || bookingInfo.totalAmount}
                            isLoading={processingPayment}
                            availableOptions={paymentMethodOptions}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Info Card - only for corporate staff (not admins) */}
                {(corporateMemberId || isCorporateBookingFlow) && corporateRole !== 'admin' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">💡 Tip:</span> You can switch between payment modes and methods anytime before confirming the booking.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Pay Button */}
            <button
              onClick={initiatePayment}
              disabled={processingPayment || (!corporateMemberId && !isCorporateBookingFlow ? !selectedPaymentMethod : (!paymentMode || !selectedPaymentMethod))}
              className="hidden md:block w-full bg-blue-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              {processingPayment ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : selectedPaymentMethod === "COD" ? (
                `Confirm Booking ₹${totalDue.toLocaleString("en-IN")} via Cash`
              ) : selectedPaymentMethod === "WALLET" ? (
                `Pay ₹${totalDue.toLocaleString("en-IN")} from Wallet`
              ) : selectedPaymentMethod === "COMPANY" ? (
                `Confirm Booking ₹${totalDue.toLocaleString("en-IN")} via Company`
              ) : selectedPaymentMethod === "ONLINE" ? (
                `Pay With Razorpay`
              ) : (
                "Select Payment Method"
              )}
            </button>
          </div>

          {/* RIGHT SIDE PRICE */}
          <div className="hidden lg:block">
            <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 sticky top-6">
              <h3 className="text-sm font-semibold text-gray-600 tracking-wide mb-4 uppercase">
                Fare Breakup
              </h3>

              <div className="bg-[#EEF2F9] rounded-xl p-4 text-[13px]">
                <div className="space-y-2.5">
                  <div className="flex justify-between text-gray-800">
                    <span>Base Fare</span>
                    <span className="font-bold text-[14px]">₹{bookingInfo.amount?.toLocaleString("en-IN")}</span>
                  </div>

                  {promoApplied && (
                    <div className="flex justify-between text-gray-800">
                      <span>Discount ({promoCode || "applied"})</span>
                      <span className="font-bold text-red-600 text-[14px]">-₹{promoDiscount.toLocaleString("en-IN")}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-gray-800">
                    <span>GST (18%)</span>
                    <span className="font-bold text-[14px]">₹{bookingInfo.taxAmount?.toLocaleString("en-IN")}</span>
                  </div>

                  {/* Individual Add-ons Display */}
                  {bookingInfo.selectedAddOns && Array.isArray(bookingInfo.selectedAddOns) && bookingInfo.selectedAddOns.length > 0 && (
                    <div className="space-y-1 mt-1 pt-1">
                      {bookingInfo.selectedAddOns.map((addOn, index) => (
                        <div key={index} className="flex justify-between text-gray-800">
                          <span>{addOn.name || addOn.description || "Add-on"}</span>
                          <span className="font-bold text-[14px]">
                            ₹{(addOn.price || 0).toLocaleString("en-IN")}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fallback for add-ons cost */}
                  {bookingInfo.addOnsCost > 0 && (!bookingInfo.selectedAddOns || bookingInfo.selectedAddOns.length === 0) && (
                    <div className="flex justify-between text-gray-800">
                      <span>Add-ons</span>
                      <span className="font-bold text-[14px]">₹{bookingInfo.addOnsCost?.toLocaleString("en-IN")}</span>
                    </div>
                  )}

                  <hr className="border-gray-300 my-2" />

                  <div className="flex justify-between items-center text-gray-900 mt-2">
                    <span className="font-medium text-[14px]">{paymentOption === "partial" ? "Total Ride Fare" : "Total Due"}</span>
                    <span className={`font-bold text-[18px] ${paymentOption === "partial" ? "text-gray-900" : "text-green-700"}`}>
                      ₹{bookingInfo.totalAmount?.toLocaleString("en-IN")}
                    </span>
                  </div>

                  {paymentOption === "partial" && (
                    <>
                      <hr className="border-gray-300 my-2" />
                      {isCodWithPartial ? (
                        <div className="flex justify-between items-center text-gray-900">
                          <span className="font-bold text-[14px] text-orange-600">Pay to Driver (Full — COD)</span>
                          <span className="font-bold text-[18px] text-green-700">₹{totalAmount.toLocaleString("en-IN")}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-center text-gray-900">
                            <span className="font-bold text-[14px] text-blue-600">Part Pay (Advance)</span>
                            <span className="font-bold text-[18px] text-green-700">₹{partialAmount.toLocaleString("en-IN")}</span>
                          </div>
                          <div className="flex justify-between items-center text-gray-600 mt-1">
                            <span className="text-[13px] font-medium">Pay to Driver</span>
                            <span className="font-bold text-[14px] text-gray-900">₹{remainingAmount.toLocaleString("en-IN")}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secured Payment Footer */}
        <div className="flex flex-col lg:flex-row items-center justify-between py-6 border-t border-gray-300 gap-6 mt-12 mb-8">
          <div className="flex items-center gap-3 w-max flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#008c85] flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="white" />
                <path d="M10 15l-3-3 1.4-1.4 1.6 1.6 4.6-4.6L16 9l-6 6z" fill="#008c85" />
              </svg>
            </div>
            <div>
              <p className="text-[#008c85] font-semibold text-[15px] leading-tight">CabZigo IS SECURED</p>
              <p className="text-[#008c85] font-semibold text-[15px] leading-tight">100% RBI Compliant</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 lg:mx-auto">
            {/* PCI DSS */}
            <div className="flex items-center">
              <div className="bg-[#145a55] text-white font-bold text-[12px] px-1 py-0.5 transform -skew-x-12">PCI</div>
              <div className="text-[#145a55] font-bold text-[12px] ml-1">
                <div className="leading-none">DSS</div>
                <div className="text-[7px] tracking-widest leading-none mt-[1px]">COMPLIANT</div>
              </div>
            </div>

            {/* Verisign */}
            <div className="flex items-center gap-1 border border-[#a6192e] rounded-full pl-0.5 pr-2 py-0.5">
              <span className="text-[#a6192e] w-4 h-4 flex items-center justify-center text-[14px] font-bold">✓</span>
              <div className="text-[#a6192e] text-[8px] leading-tight flex flex-col items-start border-l border-[#a6192e] pl-1">
                <span>VeriSign</span>
                <span>Secured</span>
              </div>
            </div>

            {/* Verified by VISA */}
            <div className="flex flex-col items-center italic text-[#1a3b82] leading-none">
              <span className="text-[9px] font-medium text-[#1a3b82]">Verified by</span>
              <span className="text-xl font-bold tracking-tighter">VISA</span>
            </div>

            {/* MasterCard SecureCode */}
            <div className="flex flex-col items-center leading-none">
              <span className="text-[#cc0000] font-bold italic text-[12px]">MasterCard.</span>
              <span className="text-[#ff9900] font-bold italic text-[10px]">SecureCode.</span>
            </div>

            {/* Amex SafeKey */}
            <div className="flex flex-col items-center leading-none -mt-1 lg:mr-4">
              <span className="text-[#1a3b82] font-bold text-[8px] tracking-wide uppercase">American Express</span>
              <span className="text-gray-500 font-bold text-[15px]">SafeKey<sup className="text-[8px]">®</sup></span>
            </div>
          </div>

          <div className="hidden lg:block h-12 w-[1px] bg-gray-400 mx-2"></div>

          <div className="max-w-[280px] text-[12px] text-gray-800 leading-[1.4] pr-1">
            By continuing to pay, I understand and agree with <br />
            <a href="/terms" className="text-[#008cff] hover:underline">Terms of Service</a>,{" "}
            <a href="/privacy" className="text-[#008cff] hover:underline">Privacy Policy</a> and{" "}
            <a href="/terms" className="text-[#008cff] hover:underline">User Agreement</a> <br />
            of <span className="text-[#008cff] font-medium">Cab</span><span className="text-orange-500 font-medium">Zigo</span>.
          </div>
        </div>
      </div>

      {/* MOBILE STICKY BOTTOM CHECKOUT BAR */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between shadow-[0_-4px_10px_rgba(0,0,0,0.08)]">
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-xs text-gray-500 font-semibold">
              {isCodWithPartial ? "Full (COD):" : paymentOption === "partial" ? "Advance:" : "Total:"}
            </span>
            <span className="text-xl font-extrabold text-gray-900">
              ₹{totalDue.toLocaleString("en-IN")}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileBreakupOpen(true)}
            className="text-xs text-blue-600 font-bold hover:underline active:text-blue-700 block text-left"
          >
            View Breakup
          </button>
        </div>
        <button
          type="button"
          onClick={initiatePayment}
          disabled={processingPayment || (!corporateMemberId && !isCorporateBookingFlow ? !selectedPaymentMethod : (!paymentMode || !selectedPaymentMethod))}
          className="bg-gradient-to-r from-orange-500 to-orange-600 active:from-orange-600 active:to-orange-700 text-white font-bold px-6 py-3 rounded-xl text-[14px] shadow-md transition-all active:scale-95 flex-shrink-0 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
        >
          {processingPayment ? (
            <div className="flex items-center justify-center gap-1.5">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              <span>Processing...</span>
            </div>
          ) : selectedPaymentMethod === "COD" ? (
            "Confirm (Cash)"
          ) : selectedPaymentMethod === "WALLET" ? (
            "Pay from Wallet"
          ) : selectedPaymentMethod === "COMPANY" ? (
            "Pay via Company"
          ) : selectedPaymentMethod === "ONLINE" ? (
            "Pay With Razorpay"
          ) : (
            "Select Method"
          )}
        </button>
      </div>

      {/* MOBILE FARE BREAKUP BOTTOM DRAWER */}
      {isMobileBreakupOpen && (
        <div className="lg:hidden fixed inset-0 z-[100] overflow-hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 transition-opacity" 
            onClick={() => setIsMobileBreakupOpen(false)}
          ></div>
          
          {/* Bottom Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[24px] max-h-[85vh] overflow-y-auto shadow-[0_-8px_30px_rgb(0,0,0,0.15)] p-6 transition-transform duration-300 transform translate-y-0 z-10">
            {/* Handle Bar */}
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-5 cursor-pointer" onClick={() => setIsMobileBreakupOpen(false)}></div>
            
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-extrabold text-gray-900">Fare Breakup</h3>
              <button 
                type="button" 
                onClick={() => setIsMobileBreakupOpen(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg p-1"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3.5 text-[13.5px] text-gray-800">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Base Fare</span>
                <span className="font-bold text-gray-900">₹{bookingInfo.amount?.toLocaleString("en-IN")}</span>
              </div>

              {promoApplied && (
                <div className="flex justify-between items-center text-red-600 font-semibold">
                  <span>Discount ({promoCode || "applied"})</span>
                  <span>-₹{promoDiscount.toLocaleString("en-IN")}</span>
                </div>
              )}

              {/* Individual Add-ons Display */}
              {bookingInfo.selectedAddOns && Array.isArray(bookingInfo.selectedAddOns) && bookingInfo.selectedAddOns.length > 0 && (
                <div className="space-y-2 pt-1 border-t border-gray-100">
                  {bookingInfo.selectedAddOns.map((addOn, index) => (
                    <div key={index} className="flex justify-between items-center text-gray-700">
                      <span>{addOn.name || addOn.description || "Add-on"}</span>
                      <span className="font-bold text-gray-950">
                        ₹{(addOn.price || 0).toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback for add-ons cost */}
              {bookingInfo.addOnsCost > 0 && (!bookingInfo.selectedAddOns || bookingInfo.selectedAddOns.length === 0) && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Add-ons</span>
                  <span className="font-bold text-gray-900">₹{bookingInfo.addOnsCost?.toLocaleString("en-IN")}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-gray-600">Taxes & Fees (18% GST)</span>
                <span className="font-bold text-gray-900">₹{bookingInfo.taxAmount?.toLocaleString("en-IN")}</span>
              </div>

              <hr className="border-gray-200 my-3" />

              <div className="flex justify-between items-center text-gray-900">
                <span className="font-bold text-[15px]">{paymentOption === "partial" ? "Total Ride Fare" : "Total Due"}</span>
                <span className={`font-extrabold text-lg ${paymentOption === "partial" ? "text-gray-900" : "text-green-700"}`}>
                  ₹{bookingInfo.totalAmount?.toLocaleString("en-IN")}
                </span>
              </div>

              {paymentOption === "partial" && (
                <>
                  <hr className="border-gray-100 my-2" />
                  {isCodWithPartial ? (
                    <div className="flex justify-between items-center text-gray-900">
                      <span className="font-bold text-[15px] text-orange-600">Pay to Driver (Full — COD)</span>
                      <span className="font-extrabold text-lg text-green-700">₹{totalAmount.toLocaleString("en-IN")}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-center text-gray-900">
                        <span className="font-bold text-[15px] text-blue-600">Part Pay (Advance)</span>
                        <span className="font-extrabold text-lg text-green-700">₹{partialAmount.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="flex justify-between items-center text-gray-650">
                        <span className="text-[13.5px] font-medium">Pay to Driver</span>
                        <span className="font-extrabold text-base text-gray-950">₹{remainingAmount.toLocaleString("en-IN")}</span>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => setIsMobileBreakupOpen(false)}
              className="w-full mt-6 bg-gradient-to-r from-blue-900 to-indigo-900 hover:from-blue-800 hover:to-indigo-800 text-white font-bold py-3 rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PaymentPage;
